import { useRouter } from "expo-router";
import { useAtomValue } from "jotai";
import { DateTime } from "luxon";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  View,
} from "react-native";

import type { AutoSave } from "@/hooks/use-event-auto-save";
import type { EventFormValues } from "@/types/event";
import type { DisplayEvent } from "@/utils/event-helpers";
import type { EventGuest } from "@/utils/event-queries";

import { sessionAtom } from "@/atoms/session";
import AddToCalendar from "@/components/add-to-calendar";
import { DarkBlurCard } from "@/components/DarkBlurCard";
import DisplayDescription from "@/components/DisplayDescription";
import BottomEventButton from "@/components/event/BottomEventButton";
import GuestListModal from "@/components/event/guest-list-modal";
import RsvpModal from "@/components/event/rsvp-modal";
import { SectionEditModal } from "@/components/event/section-edit-modal";
import { GText } from "@/components/g-text";
import AppModal from "@/components/modal";
import OpenDirections from "@/components/open-directions";
import SignInModal from "@/components/sign-in/sign-in-modal";
import { CalendarIcon } from "@/components/ui/calendar-icon";
import { PinIcon } from "@/components/ui/pin-icon";
import { TicketIcon } from "@/components/ui/ticket-icon";
import { colors } from "@/constants/colors";
import Bugsnag from "@/utils/bugsnag";
import {
  composeAddress,
  formatEventDate,
  formatGuestList,
  formatHostsBeingShown,
  formatPriceEditMode,
  formatPriceViewMode,
  formatTimeRange,
} from "@/utils/copy";
import { upsertRsvpStatus } from "@/utils/event-queries";
import { ACCEPT_STRING, validateAndUploadImage } from "@/utils/image-upload";

const HEADER_HEIGHT = 60;

const HORIZONTAL_MARGIN = 10;
// ── Types ────────────────────────────────────────────────────────────

export const EMPTY_FORM: EventFormValues = {
  currency: "usd",
  description: "",
  duration_minutes: "120",
  flyer_url: "",
  location_city: "",
  location_country: "US",
  location_formatted_address: "",
  location_lat: "",
  location_lng: "",
  location_name: "",
  location_neighborhood: "",
  location_place_id: "",
  location_postal_code: "",
  location_state: "",
  location_street: "",
  price_cents: "1000",
  price_service_fee: "0",
  quantity_total: "10",
  starts_at: "",
  status: "draft",
  ticket_description: "",
  ticket_name: "",
  timezone: "America/New_York",
  title: "",
  visibility: "public",
};

interface EventDisplayProps {
  autoSave?: AutoSave;
  event?: DisplayEvent;
  guests: EventGuest[];
  hosts: string[];
  initialValues?: Partial<EventFormValues>;
  mode: "create" | "edit" | "view";
  myRsvp: { role: string; rsvp_status: string } | null;
  onFlyerUrlChange?: (url: string) => void;
  onGetTicket?: () => void;
  onRefreshMember?: (userId: string) => Promise<void>;
  onRemoveTicket?: () => void;
  onShareEvent?: () => void;
  paymentUI?: React.ReactNode;
  submitLabel?: string;
  ticketLoading?: boolean;
}

// ── Helpers ──────────────────────────────────────────────────────────

export function canPublish(values: EventFormValues): boolean {
  return !!(
    values.title.trim() &&
    values.starts_at &&
    values.duration_minutes &&
    values.location_name.trim()
  );
}

export function EventDisplay({
  autoSave,
  event,
  guests,
  hosts,
  initialValues,
  mode,
  myRsvp,
  onFlyerUrlChange,
  onGetTicket,
  onRefreshMember,
  onRemoveTicket,
  onShareEvent,
  paymentUI,
  ticketLoading,
}: EventDisplayProps) {
  const vScale = 1;
  const iconSize = 32;
  const iconColWidth = 40;
  const router = useRouter();
  const sessionState = useAtomValue(sessionAtom);
  const session = sessionState === "loading" ? null : sessionState;
  const editable = mode !== "view";
  const viewCursor =
    Platform.OS === "web" ? { cursor: "default" as const } : {};

  // State for edit/create — when autoSave is provided, it owns values/saving/error
  const [localValues, setLocalValues] = useState<EventFormValues>(() => ({
    ...EMPTY_FORM,
    ...initialValues,
  }));
  const [localError, setLocalError] = useState<null | string>(null);
  const values = autoSave?.values ?? localValues;
  const saving = autoSave?.saving ?? false;
  const error = autoSave?.error ?? localError;

  const [modalSection, setModalSection] = useState<
    "date" | "description" | "location" | "ticket" | "title" | null
  >(null);
  const [guestListVisible, setGuestListVisible] = useState(false);
  const [rsvpVisible, setRsvpVisible] = useState(false);
  const [signInVisible, setSignInVisible] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<
    "compressing" | "idle" | "uploading"
  >("idle");
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Auto-detect timezone for new events (create mode only)
  const detectTimezone = useCallback(() => {
    if (editable && !autoSave && !initialValues?.timezone) {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      setLocalValues((v) => ({ ...v, timezone: tz }));
    }
  }, [editable, autoSave, initialValues?.timezone]);

  useEffect(() => {
    detectTimezone();
  }, [detectTimezone]);

  // Sync local form values when exiting edit mode (create mode fallback)
  useEffect(() => {
    if (!autoSave && !editable && initialValues) {
      setLocalValues((v) => ({ ...v, ...initialValues }));
    }
  }, [autoSave, editable, initialValues]);

  const myStatus = myRsvp?.rsvp_status ?? null;
  const isAdmin = myRsvp?.role === "admin";

  const patchValues = useCallback(
    (patch: Partial<EventFormValues>) => {
      if (autoSave) {
        autoSave.patch(patch);
      } else {
        setLocalValues((v) => ({ ...v, ...patch }));
      }
    },
    [autoSave],
  );

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setUploadProgress(0);
    setUploadStatus("idle");
    setLocalError(null);
    try {
      const url = await validateAndUploadImage(
        file,
        session!.access_token,
        (p) => setUploadProgress(p),
        (status) => setUploadStatus(status),
      );
      patchValues({ flyer_url: url });
      onFlyerUrlChange?.(url);
    } catch (err) {
      Bugsnag.notify(err instanceof Error ? err : new Error(String(err)));
      setLocalError(
        err instanceof Error ? err.message.toLowerCase() : "upload failed",
      );
    } finally {
      setUploading(false);
      setUploadStatus("idle");
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleRsvp = async (choice: "cancelled" | "confirmed" | "maybe") => {
    Bugsnag.leaveBreadcrumb("[handleRsvp] called", { choice });
    setRsvpVisible(false);
    if (!event || !session) {
      Bugsnag.leaveBreadcrumb("[handleRsvp] bailing early", {
        hasEvent: String(!!event),
        hasSession: String(!!session),
      });
      return;
    }
    Bugsnag.leaveBreadcrumb("[handleRsvp] calling upsertRsvpStatus", {
      choice,
      eventId: event.id,
      userId: session.user.id,
    });
    const ok = await upsertRsvpStatus(event.id, session.user.id, choice);
    Bugsnag.leaveBreadcrumb("[handleRsvp] upsertRsvpStatus returned", {
      ok: String(ok),
    });
    if (ok) {
      Bugsnag.leaveBreadcrumb("[handleRsvp] calling onRefreshMember");
      await onRefreshMember?.(session.user.id);
      Bugsnag.leaveBreadcrumb("[handleRsvp] onRefreshMember done");
    }
  };

  // Derive display values — view mode uses event, edit/create uses form values
  const displayTitle = mode === "view" ? event!.title : values.title;
  const displayStartISO = mode === "view" ? event!.startISO : values.starts_at;
  const displayEndISO =
    mode === "view"
      ? event!.endISO
      : (() => {
          const start = DateTime.fromISO(values.starts_at);
          if (!start.isValid) return "";
          const dur = parseInt(values.duration_minutes, 10) || 120;
          return start.plus({ minutes: dur }).toISO()!;
        })();
  const hasTicket = event?.ticketKindId != null;
  const isConfirmed = mode === "view" && myStatus === "confirmed";
  const displayVenue =
    mode === "view"
      ? isConfirmed
        ? event!.venue.toLowerCase()
        : [event!.locationNeighborhood, event!.locationCity]
            .filter(Boolean)
            .join(", ")
            .toLowerCase() || event!.venue.toLowerCase()
      : (values.location_name || "location").toLowerCase();
  const displayAddress =
    mode === "view"
      ? isConfirmed
        ? event!.address
        : "address shown after rsvp"
      : composeAddress(values) || "add address";
  const displayPrice =
    mode === "view"
      ? formatPriceViewMode(event!.priceCents, event!.priceServiceFee)
      : values.price_cents === "0"
        ? "free"
        : `you'll get ${formatPriceEditMode(values.price_cents)} per person`;
  const displayPriceDesc =
    mode === "view"
      ? event!.ticketsRemaining != null && event!.ticketsRemaining < 20
        ? `${event!.ticketsRemaining} left`
        : event!.priceServiceFee > 0
          ? `includes $${event!.priceServiceFee % 100 === 0 ? event!.priceServiceFee / 100 : (event!.priceServiceFee / 100).toFixed(2)} service cost`
          : event!.priceDescription
      : values.price_cents && values.price_cents !== "0"
        ? "a service fee will be added to cover processing costs"
        : null;
  const displayDescription =
    mode === "view" ? event!.description : values.description;
  // Info row action text
  const dateAction = mode === "view" ? "add to calendar" : "edit";
  const venueAction =
    mode === "view"
      ? isConfirmed
        ? "directions"
        : hasTicket
          ? "get ticket"
          : "rsvp"
      : "edit";
  const priceAction =
    mode === "view" ? (isConfirmed ? "share event" : "get ticket") : "edit";

  // Status pill helpers
  const originalStatus = initialValues?.status ?? "draft";

  // Wrap info rows for view mode actions vs edit mode modals
  const wrapDateRow = (children: React.ReactNode) => {
    if (mode === "view" && event) {
      return (
        <AddToCalendar
          description={event.description}
          endISO={event.endISO ?? ""}
          geo={event.geo}
          location={event.address}
          startISO={event.startISO ?? ""}
          title={event.title}
          venue={event.venue}
        >
          {children}
        </AddToCalendar>
      );
    }
    return (
      <Pressable
        onPress={() => {
          setModalSection("date");
        }}
        style={({ pressed }: { hovered?: boolean; pressed: boolean }) => [
          pressed && styles.rowHighlight,
        ]}
      >
        {children}
      </Pressable>
    );
  };

  const wrapVenueRow = (children: React.ReactNode) => {
    if (mode === "view" && event) {
      if (isConfirmed) {
        return (
          <OpenDirections
            label={event.venue}
            lat={event.geo.lat}
            lng={event.geo.lng}
            placeId={event.placeId}
          >
            {children}
          </OpenDirections>
        );
      }
      return (
        <Pressable
          onPress={hasTicket ? onGetTicket : () => setRsvpVisible(true)}
        >
          {children}
        </Pressable>
      );
    }
    return (
      <Pressable
        onPress={() => {
          setModalSection("location");
        }}
        style={({ pressed }: { hovered?: boolean; pressed: boolean }) => [
          pressed && styles.rowHighlight,
        ]}
      >
        {children}
      </Pressable>
    );
  };

  const wrapPriceRow = (children: React.ReactNode) => {
    if (mode === "view") {
      return (
        <Pressable onPress={isConfirmed ? onShareEvent : onGetTicket}>
          {children}
        </Pressable>
      );
    }
    return (
      <Pressable
        onPress={() => {
          if (!hasTicket) {
            patchValues({ price_cents: "500", quantity_total: "7" });
          }
          setModalSection("ticket");
        }}
        style={({ pressed }: { hovered?: boolean; pressed: boolean }) => [
          pressed && styles.rowHighlight,
        ]}
      >
        {children}
      </Pressable>
    );
  };

  const getBottomLabel = useMemo(() => {
    if (values.status === "cancelled") return "back to events";

    Bugsnag.leaveBreadcrumb("event form values", {
      status: values.status ?? "",
    });
    if (mode === "view") {
      if (!session) return "login";
      if (isAdmin && values.status !== "published") return "continue editing";
      if (ticketLoading) return "processing...";

      if (!isConfirmed) {
        if (hasTicket) {
          if (isAdmin) {
            return "rsvp";
          }
          return "get ticket";
        }
        return "rsvp";
      }
      return "share event";
    }
    if (!values.title.trim()) return "add title";
    if (!values.starts_at || !values.duration_minutes) return "add date";
    if (!values.location_name.trim()) return "add location";
    if (values.status !== "published") return "publish event";
    return "finish with edits";
  }, [
    hasTicket,
    isAdmin,
    isConfirmed,
    mode,
    session,
    ticketLoading,
    values.duration_minutes,
    values.location_name,
    values.starts_at,
    values.status,
    values.title,
  ]);

  const handleBottomOnPress = () => {
    switch (getBottomLabel) {
      case "add date":
        setModalSection("date");
        break;
      case "add location":
        setModalSection("location");
        break;
      case "add title":
        setModalSection("title");
        break;
      case "back to events":
        router.navigate("/");
        break;
      case "continue editing":
        router.setParams({ mode: "edit" });
        break;
      case "finish with edits":
        router.setParams({ mode: undefined });
        break;
      case "get ticket":
        onGetTicket?.();
        break;
      case "login":
        setSignInVisible(true);
        break;
      case "publish event":
        patchValues({ status: "published" });
        break;
      case "rsvp":
        setRsvpVisible(true);
        break;
      case "share event":
        onShareEvent?.();
        break;
    }
  };

  return (
    <View style={styles.container}>
      {/* Fixed header */}
      <View style={styles.fixedHeader}>
        <Pressable
          onPress={() => {
            if (editable) {
              router.setParams({ mode: undefined });
            } else {
              router.navigate("/e");
            }
          }}
          style={[styles.editButton, { marginRight: "auto" }]}
        >
          <GText style={styles.editText}>
            {!editable ? "see all events" : "back to view mode"}
          </GText>
        </Pressable>
        <View style={styles.headerSpacer} />

        {isAdmin && !editable && (
          <Pressable
            onPress={() => router.setParams({ mode: "edit" })}
            style={[styles.editButton, { marginLeft: "auto" }]}
          >
            <GText style={styles.editText}>edit</GText>
          </Pressable>
        )}
        {isAdmin && editable && originalStatus !== "cancelled" && (
          <Pressable
            onPress={() => setShowCancelConfirm(true)}
            style={[styles.statusPill, { marginLeft: "auto" }]}
          >
            <GText style={styles.statusPillText}>cancel event</GText>
          </Pressable>
        )}
        {isAdmin && editable && originalStatus === "cancelled" && (
          <View
            style={[
              styles.statusPill,
              styles.statusPillCancelled,
              { marginLeft: "auto" },
            ]}
          >
            <GText
              style={[styles.statusPillText, styles.statusPillTextCancelled]}
            >
              event cancelled
            </GText>
          </View>
        )}
      </View>

      <ScrollView
        contentContainerStyle={[
          styles.content,
          {
            marginBottom: 100,
            marginTop: HEADER_HEIGHT,
            maxWidth: 600,
          },
        ]}
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}
        style={[styles.scroll]}
      >
        <DarkBlurCard style={{ padding: 20, width: "100%" }}>
          {/* Title */}
          <View style={styles.headerRow}>
            <Pressable
              disabled={!editable}
              onPress={() => setModalSection("title")}
              style={{ flexShrink: 1 }}
            >
              <GText
                nativeID="event-title"
                style={[
                  styles.title,
                  !editable && viewCursor,
                  editable && { color: colors.white },
                  editable && !values.title && { opacity: 0.5 },
                ]}
              >
                {editable ? values.title || "title" : displayTitle}
              </GText>
            </Pressable>
          </View>

          {/* Hosted by */}
          {hosts.length > 0 && (
            <GText style={[styles.hostedBy, viewCursor]}>
              {formatHostsBeingShown(hosts)}
            </GText>
          )}

          {/* Info grid */}
          <View style={styles.infoGrid}>
            {/* Date row */}
            {wrapDateRow(
              <View style={[styles.infoRow, { marginBottom: 12 * vScale }]}>
                <View style={[styles.iconColumn, { width: iconColWidth }]}>
                  <CalendarIcon color={colors.white} size={iconSize} />
                </View>
                <View style={styles.textColumn}>
                  <GText
                    style={[styles.detailPrimary, { fontSize: 15 * vScale }]}
                  >
                    {displayStartISO
                      ? formatEventDate(displayStartISO)
                      : "date"}
                  </GText>
                  {displayStartISO ? (
                    <GText
                      style={[
                        styles.detailSecondary,
                        { fontSize: 14 * vScale },
                      ]}
                    >
                      {formatTimeRange(displayStartISO, displayEndISO)}
                    </GText>
                  ) : null}
                </View>
                <GText style={[styles.actionText, { fontSize: 14 * vScale }]}>
                  {dateAction}
                </GText>
              </View>,
            )}

            {/* Venue row */}
            {wrapVenueRow(
              <View style={[styles.infoRow, { marginBottom: 12 * vScale }]}>
                <View style={[styles.iconColumn, { width: iconColWidth }]}>
                  <PinIcon color={colors.white} size={iconSize} />
                </View>
                <View style={styles.textColumn}>
                  <GText
                    style={[styles.detailPrimary, { fontSize: 15 * vScale }]}
                  >
                    {displayVenue}
                  </GText>
                  <GText
                    style={[styles.detailSecondary, { fontSize: 14 * vScale }]}
                  >
                    {displayAddress}
                  </GText>
                </View>
                <GText style={[styles.actionText, { fontSize: 14 * vScale }]}>
                  {venueAction}
                </GText>
              </View>,
            )}

            {/* Price row — hidden when no ticket exists (unless editable) */}
            {hasTicket
              ? wrapPriceRow(
                  <View style={[styles.infoRow, { marginBottom: 12 * vScale }]}>
                    <View style={[styles.iconColumn, { width: iconColWidth }]}>
                      <TicketIcon color={colors.white} size={iconSize} />
                    </View>
                    <View style={styles.textColumn}>
                      <GText
                        style={[
                          styles.detailPrimary,
                          { fontSize: 15 * vScale },
                        ]}
                      >
                        {displayPrice}
                      </GText>
                      {displayPriceDesc ? (
                        <GText
                          style={[
                            styles.detailSecondary,
                            { fontSize: 14 * vScale },
                          ]}
                        >
                          {displayPriceDesc}
                        </GText>
                      ) : null}
                    </View>
                    <GText
                      style={[styles.actionText, { fontSize: 14 * vScale }]}
                    >
                      {priceAction}
                    </GText>
                  </View>,
                )
              : editable
                ? wrapPriceRow(
                    <View
                      style={[styles.infoRow, { marginBottom: 12 * vScale }]}
                    >
                      <View
                        style={[styles.iconColumn, { width: iconColWidth }]}
                      >
                        <TicketIcon color={colors.white} size={iconSize} />
                      </View>
                      <View style={styles.textColumn}>
                        <GText
                          style={[
                            styles.detailPrimary,
                            { fontSize: 15 * vScale, opacity: 0.5 },
                          ]}
                        >
                          + add ticketing
                        </GText>
                      </View>
                    </View>,
                  )
                : null}
          </View>

          {/* Visibility + RSVP row */}
          <View style={styles.statusRow}>
            {editable ? (
              <>
                <GText style={styles.statusLabel}>visibility:</GText>
                <View style={styles.statusPillRow}>
                  {(
                    [
                      { label: "open to all", value: "public" },
                      {
                        label: "invite only",
                        value: "invite_only",
                      },
                    ] as const
                  ).map((opt) => {
                    const isActive = values.visibility === opt.value;
                    return (
                      <Pressable
                        disabled={isActive}
                        key={opt.value}
                        onPress={() => patchValues({ visibility: opt.value })}
                        style={[
                          styles.statusPill,
                          isActive && styles.statusPillActive,
                        ]}
                      >
                        <GText
                          style={[
                            styles.statusPillText,
                            isActive && styles.statusPillTextActive,
                          ]}
                        >
                          {opt.label}
                        </GText>
                      </Pressable>
                    );
                  })}
                </View>
                <GText style={[styles.statusLabel, { opacity: 0 }]}>
                  event visibility:
                </GText>
              </>
            ) : (
              <>
                {session ? (
                  guests.length > 0 ? (
                    <Pressable
                      onPress={() => setGuestListVisible(true)}
                      style={{ flex: 1 }}
                    >
                      <GText style={styles.rsvpNames}>
                        {formatGuestList(guests)}
                      </GText>
                    </Pressable>
                  ) : (
                    <View style={{ flex: 1 }} />
                  )
                ) : (
                  <Pressable
                    onPress={() => setSignInVisible(true)}
                    style={{ flex: 1 }}
                  >
                    <GText style={styles.rsvpNames}>
                      login to see who&apos;s going
                    </GText>
                  </Pressable>
                )}
                <GText style={{ color: colors.light, ...viewCursor }}>
                  {values.visibility === "invite_only"
                    ? "invitation only"
                    : "public event"}
                </GText>
              </>
            )}
          </View>

          {/* Description */}
          <View style={[styles.section, { marginBottom: 24 * vScale }]}>
            <DisplayDescription
              description={editable ? values.description : displayDescription}
              editable={editable}
              onPress={() => setModalSection("description")}
              vScale={vScale}
            />
          </View>

          {/* Flyer link */}
          {editable && (
            <View style={[styles.section, { marginBottom: 24 * vScale }]}>
              <Pressable
                disabled={uploading}
                onPress={() => fileInputRef.current?.click()}
              >
                <GText style={styles.flyerLinkText}>
                  {uploading
                    ? uploadStatus === "compressing"
                      ? "compressing..."
                      : `uploading ${uploadProgress}%`
                    : values.flyer_url
                      ? "+ edit background image"
                      : "+ add background image"}
                </GText>
              </Pressable>
              {Platform.OS === "web" && (
                <input
                  accept={ACCEPT_STRING}
                  onChange={handleFileSelect}
                  ref={fileInputRef}
                  style={styles.hidden}
                  type="file"
                />
              )}
            </View>
          )}
          {isAdmin && <GText style={styles.statusText}>{values.status}</GText>}

          {error ? <GText style={styles.error}>{error}</GText> : null}
        </DarkBlurCard>
      </ScrollView>

      {/* Bottom button */}
      <BottomEventButton
        disabled={saving || getBottomLabel === "processing..."}
        label={getBottomLabel}
        onPress={handleBottomOnPress}
        saving={saving && mode !== "view"}
        vScale={vScale}
      />

      {mode === "view" && paymentUI}

      {/* Guest list modal */}
      {mode === "view" && (
        <GuestListModal
          eventId={event?.id ?? ""}
          guests={guests}
          isAdmin={isAdmin}
          onClose={() => setGuestListVisible(false)}
          visible={guestListVisible}
        />
      )}

      {/* RSVP modal (no-ticket events) */}
      {mode === "view" && (
        <RsvpModal
          onClose={() => setRsvpVisible(false)}
          onSelect={handleRsvp}
          visible={rsvpVisible}
        />
      )}

      {/* Sign in modal */}
      <SignInModal
        onClose={() => setSignInVisible(false)}
        onSignedIn={() => setSignInVisible(false)}
        visible={signInVisible}
      />

      {/* Cancel event confirmation */}
      <AppModal
        onClose={() => setShowCancelConfirm(false)}
        visible={showCancelConfirm}
      >
        <GText style={styles.cancelConfirmText}>
          are you sure you want to cancel the event?
        </GText>
        <Pressable
          onPress={() => {
            patchValues({ status: "cancelled" });
            setShowCancelConfirm(false);
          }}
          style={styles.cancelConfirmButton}
        >
          <GText style={styles.cancelConfirmButtonText}>confirm</GText>
        </Pressable>
      </AppModal>

      {/* Section edit modals (edit/create only) */}
      {editable && (
        <SectionEditModal
          hasExistingTicket={hasTicket}
          onChange={(patch) => {
            patchValues(patch);
          }}
          onClose={() => setModalSection(null)}
          onRemoveTicket={
            onRemoveTicket
              ? () => {
                  patchValues({
                    price_cents: "",
                    quantity_total: "",
                    ticket_description: "",
                    ticket_name: "",
                  });
                  onRemoveTicket();
                  setModalSection(null);
                }
              : undefined
          }
          section={modalSection}
          values={values}
        />
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  actionText: {
    color: colors.white,
    fontSize: 14,
    fontWeight: "600",
    marginLeft: 8,
    textAlign: "right",
  },
  bottomButton: {
    alignItems: "center",
    alignSelf: "center",
    backgroundColor: colors.white,
    borderRadius: 28,
    marginBottom: 32,
    maxWidth: 600,
    paddingHorizontal: 32,
    paddingVertical: 16,
    width: "100%",
  },
  bottomContainer: {
    bottom: 0,
    left: 0,
    paddingHorizontal: 24,
    paddingTop: 12,
    position: "absolute",
    right: 0,
  },
  bottomSpacer: {
    height: 80,
  },
  bottomText: {
    color: colors.black,
    fontSize: 18,
    fontWeight: "600",
  },
  cancelConfirmButton: {
    alignItems: "center" as const,
    backgroundColor: colors.red,
    borderRadius: 12,
    marginTop: 16,
    paddingVertical: 12,
  },
  cancelConfirmButtonText: {
    color: colors.white,
    fontSize: 16,
    fontWeight: "600" as const,
  },
  cancelConfirmText: {
    color: colors.white,
    fontSize: 18,
    textAlign: "center" as const,
  },
  cancelText: {
    color: colors.white,
    fontSize: 16,
    fontWeight: "600",
    width: 60,
  },
  container: {
    backgroundColor: colors.transparent,
    flex: 1,
    paddingHorizontal: HORIZONTAL_MARGIN,
  },
  content: {
    alignItems: "center",
    alignSelf: "center",
    paddingHorizontal: 24,
    width: "100%",
  },
  detailPrimary: {
    color: colors.white,
    fontSize: 18,
    fontWeight: "600",
    textAlign: "left",
  },
  detailSecondary: {
    color: colors.white,
    fontSize: 14,
    opacity: 0.8,
    textAlign: "left",
  },
  disabledOpacity: {
    opacity: 0.5,
  },
  editButton: {
    borderColor: colors.white,
    borderRadius: 16,
    borderWidth: 1,
    paddingHorizontal: 14,
    paddingVertical: 6,
  },
  editText: {
    color: colors.white,
    fontSize: 14,
    fontWeight: "600",
  },
  error: {
    color: colors.red,
    fontSize: 14,
    marginTop: 8,
  },
  fixedHeader: {
    alignItems: "center",
    alignSelf: "center",
    flexDirection: "row",
    height: HEADER_HEIGHT,
    left: 0,
    marginHorizontal: HORIZONTAL_MARGIN,
    position: "absolute",
    right: 0,
    top: 0,
    zIndex: 10,
  },
  flexFill: {
    flex: 1,
  },
  flyerLinkText: {
    color: colors.white,
    fontSize: 15,
    lineHeight: 22,
    marginBottom: 8,
    textAlign: "left",
  },
  headerContainer: {
    left: 10,
    position: "absolute",
    right: 10,
    top: 0,
  },
  headerInner: {
    alignItems: "center",
    alignSelf: "center",
    flexDirection: "row",
    height: HEADER_HEIGHT,
    justifyContent: "space-between",
    maxWidth: 600,
    width: "100%",
    zIndex: 10,
  },
  headerRow: {
    alignItems: "center",
    flexDirection: "row",
    justifyContent: "center",
    marginBottom: 8,
  },
  headerSpacer: {
    width: 50,
  },
  hidden: {
    display: "none",
  },
  hostedBy: {
    color: colors.light,
    fontSize: 14,
    marginBottom: 12,
    textAlign: "left",
    width: "100%",
  },
  iconColumn: {
    alignItems: "center",
    paddingRight: 10,
    width: 32,
  },
  infoGrid: {
    marginBottom: 16,
    width: "100%",
  },
  infoRow: {
    alignItems: "center",
    flexDirection: "row",
    marginBottom: 12,
  },
  rowHighlight: {
    backgroundColor: colors.cardBg,
    borderRadius: 12,
  },
  rsvpNames: {
    color: colors.white,
    fontSize: 15,
    fontWeight: "600",
  },
  rsvpSection: {
    marginBottom: 16,
    width: "100%",
  },
  scroll: {
    flex: 1,
  },
  section: {
    marginBottom: 24,
    width: "100%",
  },
  statusLabel: {
    color: colors.light,
    fontSize: 14,
    fontWeight: "500",
    marginRight: 20,
  },
  statusPill: {
    borderColor: colors.divider,
    borderRadius: 20,
    borderWidth: 1,
    paddingHorizontal: 16,
    paddingVertical: 8,
  },
  statusPillActive: {
    backgroundColor: colors.white,
    borderColor: colors.white,
  },
  statusPillCancelled: {
    backgroundColor: colors.red,
    borderColor: colors.red,
  },
  statusPillDisabled: {
    opacity: 0.3,
  },
  statusPillRow: {
    flexDirection: "row",
    gap: 10,
  },
  statusPillText: {
    color: colors.white,
    fontSize: 14,
    fontWeight: "500",
  },
  statusPillTextActive: {
    color: colors.black,
  },
  statusPillTextCancelled: {
    color: colors.white,
  },
  statusRow: {
    alignItems: "center",
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 16,
    width: "100%",
  },
  statusText: {
    color: colors.light,
    fontSize: 14,
    textAlign: "center",
  },
  textColumn: {
    flex: 1,
  },
  title: {
    color: colors.light,
    flex: 1,
    fontWeight: "700",
    textAlign: "left",
  },
});
